/**
 * slotGate — the client's view of the member-level slot pool (ARC-53).
 *
 * Pure. No React, no I/O. One input (`GET /status.staking`, already normalized
 * by services/engineApi) and one output: the four states the tab can be in, and
 * the counts it is allowed to render in each.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS: FAIL CLOSED ON OPEN.
 *
 * The v3 surface (ARC-32) gated rows on a direct chain read, and when that read
 * failed it fell back to a placeholder count and UNLOCKED rows. The stated
 * reasoning was that the engine enforces the real gate on every signal — but
 * ARC-43, which builds that enforcement, is not merged and ships behind a flag
 * that is off. There is no engine-side gate today. A UI that fails OPEN while
 * the engine has no gate is the worst of both: during an outage a member could
 * configure allocations their stake does not back.
 *
 * So: an answer we do not have locks. Loading locks. A failed read locks. An
 * unhealthy stake index locks. Only a healthy, complete answer unlocks, and
 * only up to the number of slots that answer names.
 *
 * The mirror rule — fail OPEN on close — is the engine's, at signal time
 * (ARC-43): a member whose stake lapses mid-trade still gets their exit. Nothing
 * in this file can close a position, and nothing here may claim it does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GATE IS READ, NEVER DERIVED. `gate` comes off the wire verbatim. Only the
 * engine knows whether a position is riding, which is the difference between
 * "closing_only" and "blocked". Deriving it here from counts would produce a
 * second opinion that disagrees with the engine exactly when it matters most.
 * Nothing below compares slotsUsed to slotsTotal to decide anything.
 *
 * COMPLIANCE: slots are a count of automation seats. No return, price,
 * projection or promise appears in this file's copy.
 */

/** What the tab knows about the member's slots right now. */
export const SLOT_GATE_STATE = {
  /** The Supernova feature is off, or there is no engine to ask. No gating. */
  OFF: "off",
  /** The first /status read is still in flight. Locked, and no digits yet. */
  LOADING: "loading",
  /** No answer, or an answer whose stake index is unhealthy. Locked. */
  UNAVAILABLE: "unavailable",
  /** A complete answer. `unlockedCount` is the engine's slot total. */
  READY: "ready",
};

/* Member-facing copy for the two states that have to explain themselves. Both
   describe a connection, not a position — nothing here is advice or a promise. */
export const SLOT_GATE_LOADING_NOTE = "Checking your Supernova stake…";
export const SLOT_GATE_UNAVAILABLE_NOTE =
  "Couldn't read your stake right now. Your stake is unaffected — strategies stay " +
  "locked until we can confirm how many slots it backs.";

/* ─────────────────────────────────────────────────────────────────────────────
 * ARC-64 — WHICH LAYER FAILED.
 *
 * UNAVAILABLE was one bucket holding five materially different failures, and it
 * said the same sentence for all of them. That is what made the reported bug
 * undiagnosable from the screen: a member (and Gven, on staging) saw "couldn't
 * read your stake" whether the engine was unreachable, answering but not
 * serving a stake pool, serving one with a sick index, or serving counts we
 * could not parse. Two of those are worth retrying and three are not, and the
 * tab offered the same "Try again" for all five — a button that cannot help is
 * its own small lie.
 *
 * The LOCK is unchanged. Every reason below still locks, still renders no
 * digit, and still says the member's stake is unaffected. This adds a second
 * sentence naming the layer, and an honest answer to "will retrying help?".
 * Nothing here loosens the fail-closed rule — see the header.
 *
 * COMPLIANCE: every string names a connection or a system state. No return,
 * price, projection, promise or advice appears in any of them.
 */
export const SLOT_GATE_REASON = {
  /** The /status read is not landing at all — network, or the engine is down. */
  ENGINE_UNREACHABLE: "engine_unreachable",
  /** The engine answered, but has no record for this wallet yet (404). */
  NOT_ENROLLED: "not_enrolled",
  /** The engine answered /status and served no `staking` block at all. */
  NO_STAKE_POOL: "no_stake_pool",
  /** A stake pool arrived, but the engine's stake index is unhealthy. */
  INDEX_UNHEALTHY: "index_unhealthy",
  /** A healthy index that still named no readable slot total. */
  COUNT_UNREADABLE: "count_unreadable",
};

/**
 * Per-reason detail + whether asking again can plausibly change the answer.
 * `retryable: false` is what removes the retry button, so it is set only where
 * pressing it genuinely cannot help: enrollment and an engine build that does
 * not serve the pool both need something OTHER than another request.
 */
const REASON_DETAIL = {
  [SLOT_GATE_REASON.ENGINE_UNREACHABLE]: {
    detail:
      "We couldn't reach the automation engine to ask. Your stake is on-chain and " +
      "unaffected by this — it's our read that failed.",
    retryable: true,
  },
  [SLOT_GATE_REASON.NOT_ENROLLED]: {
    detail:
      "The automation engine has no record for this wallet yet, so it can't report " +
      "your slots. Complete enrollment below — retrying this read won't change it.",
    retryable: false,
  },
  [SLOT_GATE_REASON.NO_STAKE_POOL]: {
    detail:
      "The automation engine answered, but didn't report a stake pool at all. That's " +
      "an engine-side gap, not something retrying will resolve.",
    retryable: false,
  },
  [SLOT_GATE_REASON.INDEX_UNHEALTHY]: {
    detail:
      "The automation engine reported that its stake index is unhealthy, so it won't " +
      "state a slot count. Your stake is on-chain and unaffected; this clears when the " +
      "index recovers.",
    retryable: true,
  },
  [SLOT_GATE_REASON.COUNT_UNREADABLE]: {
    detail:
      "The automation engine reported a healthy stake index but no slot count we could " +
      "read. Your stake is unaffected.",
    retryable: true,
  },
};

const isCount = (n) => typeof n === "number" && Number.isFinite(n) && n >= 0;

/**
 * Which layer failed, when there is no usable stake pool.
 *
 * The `staking`-present reasons are read off the payload itself. The absent
 * ones need connection context the payload cannot carry, so a caller that
 * supplies none gets `null` — and the generic note — rather than a guess. That
 * fallback is what keeps every pre-ARC-64 caller behaving exactly as before.
 */
const classifyUnavailable = ({ staking, statusSeen, connectionFailing, mode }) => {
  if (staking) {
    return staking.indexHealthy
      ? SLOT_GATE_REASON.COUNT_UNREADABLE
      : SLOT_GATE_REASON.INDEX_UNHEALTHY;
  }
  /* A read that is actively failing outranks everything else: whatever the
     engine last said, we are not hearing it now. */
  if (connectionFailing) return SLOT_GATE_REASON.ENGINE_UNREACHABLE;
  /* A /status payload arrived and simply had no staking block on it. */
  if (statusSeen) return SLOT_GATE_REASON.NO_STAKE_POOL;
  /* Reachable, answering, no payload: the 404-before-enrollment case, which
     useEngineStatus deliberately reports as a success with a null status. */
  if (mode === "live") return SLOT_GATE_REASON.NOT_ENROLLED;
  if (mode === "demo" || mode === "connecting") return SLOT_GATE_REASON.ENGINE_UNREACHABLE;
  return null;
};

/**
 * A compact, non-member-facing summary of the failed read, for the console and
 * for pasting into an issue. This is the evidence ARC-64 asks for and could not
 * get from a screen that said the same sentence in every failure.
 * Addresses are NOT included — `perWallet` holds member wallet addresses and
 * this string is written to a log.
 */
const buildDiagnostic = ({ reason, staking, statusSeen, connectionFailing, mode }) => {
  if (!reason) return null;
  const parts = [`reason=${reason}`, `statusSeen=${Boolean(statusSeen)}`];
  if (mode) parts.push(`mode=${mode}`);
  parts.push(`connectionFailing=${Boolean(connectionFailing)}`);
  if (staking) {
    parts.push(
      `staking.source=${staking.source ?? "?"}`,
      `staking.indexHealthy=${staking.indexHealthy}`,
      `staking.slotsTotal=${staking.slotsTotal === null ? "null" : staking.slotsTotal}`,
      `staking.gate=${staking.gate ?? "null"}`,
      `staking.asOfBlock=${staking.asOfBlock ?? "null"}`,
      `staking.perWalletCount=${Array.isArray(staking.perWallet) ? staking.perWallet.length : 0}`
    );
  } else {
    parts.push("staking=null");
  }
  return parts.join(" ");
};

/**
 * @param {Object} input
 * @param {boolean} input.enabled   the Supernova feature is on AND an engine is
 *        configured. False ⇒ SLOT_GATE_STATE.OFF and no row is gated: with no
 *        stake surface to act on, locking a member out would be a dead end.
 * @param {Object|null} input.staking  normalized `status.staking`; null ⇒ the
 *        engine served no slot pool (an older engine, or no record yet).
 * @param {boolean} [input.loading]  the first /status read is still in flight.
 * @param {boolean} [input.statusSeen]  a `/status` payload is in hand (ARC-64).
 *        Distinguishes "the engine answered and served no stake pool" from
 *        "we never got an answer" — same lock, different cause and cure.
 * @param {boolean} [input.connectionFailing]  the /status read is currently
 *        failing (useEngineStatus `failures > 0`).
 * @param {"demo"|"connecting"|"live"} [input.mode]  useEngineStatus's mode.
 * @returns {{
 *   state: string, engaged: boolean, countsKnown: boolean,
 *   unlockedCount: number|null,
 *   slotsTotal: number|null, slotsUsed: number|null, slotsAvailable: number|null,
 *   gate: string|null, openPositionRiding: boolean,
 *   unbackedPairs: Array<{wallet: string|null, pair: string}>,
 *   perWallet: Array<{address: string, staked: number|null, label: string|null}>,
 *   message: string|null,
 *   reason: string|null, detail: string|null, retryable: boolean|null,
 *   diagnostic: string|null,
 * }}
 */
export function deriveSlotGate(input = {}) {
  const {
    enabled = false,
    staking = null,
    loading = false,
    statusSeen = false,
    connectionFailing = false,
    mode = null,
  } = input;

  const base = {
    state: SLOT_GATE_STATE.OFF,
    engaged: false,
    countsKnown: false,
    /* null is the fail-closed value: "we cannot name a number of unlocked
       slots". Every caller must read it as lock, never as zero-then-unlock. */
    unlockedCount: null,
    slotsTotal: null,
    slotsUsed: null,
    slotsAvailable: null,
    gate: null,
    openPositionRiding: false,
    unbackedPairs: [],
    perWallet: [],
    message: null,
    /* ARC-64. Null in every state that is not a failed read — OFF, LOADING and
       READY have no layer to name. */
    reason: null,
    detail: null,
    retryable: null,
    diagnostic: null,
  };

  if (!enabled) return base;

  /* Still asking. Distinct from "asked and could not answer" because the UI
     shows a skeleton for one and an error with a retry for the other — and
     because a spinner that says "couldn't read" is a lie for the half-second
     before the answer lands. Both lock. */
  if (!staking && loading) {
    return {
      ...base,
      state: SLOT_GATE_STATE.LOADING,
      engaged: true,
      message: SLOT_GATE_LOADING_NOTE,
    };
  }

  /* No slot pool, an unhealthy index, or a total we cannot read. ARC-42 omits
     the counts rather than sending zero when its index is down; this is the
     client holding the same line. */
  if (!staking || !staking.indexHealthy || !isCount(staking.slotsTotal)) {
    /* ARC-64 — same lock, but say WHICH layer failed and whether asking again
       can help. `message` is unchanged for every caller: the reassurance the
       member already had is the headline, and the layer is the line under it. */
    const context = { staking, statusSeen, connectionFailing, mode };
    const reason = classifyUnavailable(context);
    const { detail = null, retryable = null } = REASON_DETAIL[reason] || {};

    return {
      ...base,
      state: SLOT_GATE_STATE.UNAVAILABLE,
      engaged: true,
      message: SLOT_GATE_UNAVAILABLE_NOTE,
      reason,
      detail,
      /* No classification ⇒ no claim either way. The caller keeps offering the
         retry it offered before, rather than hiding it on a guess. */
      retryable,
      diagnostic: buildDiagnostic({ reason, ...context }),
    };
  }

  return {
    state: SLOT_GATE_STATE.READY,
    engaged: true,
    countsKnown: true,
    /* One staked Supernova unlocks one automated strategy — the engine's
       `slotsTotal`, not a count this file worked out. */
    unlockedCount: staking.slotsTotal,
    slotsTotal: staking.slotsTotal,
    slotsUsed: isCount(staking.slotsUsed) ? staking.slotsUsed : null,
    slotsAvailable: isCount(staking.slotsAvailable) ? staking.slotsAvailable : null,
    /* Verbatim from the engine. */
    gate: staking.gate,
    openPositionRiding: Boolean(staking.openPositionRiding),
    unbackedPairs: Array.isArray(staking.unbackedPairs) ? staking.unbackedPairs : [],
    perWallet: Array.isArray(staking.perWallet) ? staking.perWallet : [],
    message: null,
    /* A complete answer has no failed layer to name. */
    reason: null,
    detail: null,
    retryable: null,
    diagnostic: null,
  };
}

export default deriveSlotGate;
